import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { useGuardedMutation } from "model/useGuardedMutation";
import { selectUser, useAppStore } from "stores";

// Scope invite cache keys to the current user so persisted localStorage data
// can never leak between accounts in the same browser. Falls back to "anon"
// for unauthenticated invite landing pages (which only use token-based keys).
function useInviteScope(): string {
  return useAppStore(selectUser)?.guid ?? "anon";
}

export type InviteStatus =
  | "PENDING_SIGNUP"
  | "PENDING_CONSENT"
  | "ACTIVE"
  | "EXPIRED"
  | "REVOKED";

export interface ManagedProgramme {
  guid: string;
  name: string;
  type: string;
}

export interface ProgrammeInvitation {
  guid: string;
  programmeGuid: string;
  programmeName: string;
  invitedEmail: string;
  inviteStatus: InviteStatus;
  invitedAt: string | null;
  invitedByName: string | null;
  acceptedUserGuid: string | null;
  acceptedUserName: string | null;
  consentSignedAt: string | null;
  consentFileGuid: string | null;
}

export interface InvitationTokenLookup {
  programmeName: string;
  programmeGuid: string;
  invitedEmail: string | null;
  invitedName: string | null;
  invitedTradingName: string | null;
  inviteStatus: InviteStatus;
  hasExistingAccount: boolean;
  expired: boolean;
}

const baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1`;

function authHeaders(includeJson = true): HeadersInit {
  const h: Record<string, string> = {
    Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
  };
  if (includeJson) h["Content-Type"] = "application/json";
  return h;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, { headers: authHeaders(false) });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return (await res.json()) as T;
}

async function postJSON<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function useManagedProgrammes(enabled = true) {
  const scope = useInviteScope();
  return useQuery({
    queryKey: queryKeys.programmeInvitations.managedProgrammes(scope),
    queryFn: () =>
      getJSON<ManagedProgramme[]>("/programmeInvitations/managedProgrammes"),
    enabled,
  });
}

export function useProgrammeInvitations(programmeGuid: string | null) {
  const scope = useInviteScope();
  return useQuery({
    queryKey: queryKeys.programmeInvitations.forProgramme(
      scope,
      programmeGuid ?? ""
    ),
    queryFn: () =>
      getJSON<ProgrammeInvitation[]>(`/programme/${programmeGuid}/invitations`),
    enabled: !!programmeGuid,
  });
}

/**
 * Aggregate count of farmers with a pending invite (awaiting signup or awaiting
 * consent) across all programmes the corporate user manages. Reuses the same
 * per-programme query keys as `useProgrammeInvitations`/`PendingInvitationsList`
 * so the requests dedupe rather than double-fetch.
 */
export function usePendingInvitationCount(): {
  count: number;
  isLoading: boolean;
} {
  const scope = useInviteScope();
  const { data: programmes, isLoading: programmesLoading } =
    useManagedProgrammes();
  const results = useQueries({
    queries: (programmes ?? []).map((p) => ({
      queryKey: queryKeys.programmeInvitations.forProgramme(scope, p.guid),
      queryFn: () =>
        getJSON<ProgrammeInvitation[]>(`/programme/${p.guid}/invitations`),
    })),
  });
  const isLoading = programmesLoading || results.some((r) => r.isLoading);
  const count = results
    .flatMap((r) => r.data ?? [])
    .filter(
      (i) =>
        i.inviteStatus === "PENDING_SIGNUP" ||
        i.inviteStatus === "PENDING_CONSENT"
    ).length;
  return { count, isLoading };
}

export function useMyPendingInvitations(enabled = true) {
  const scope = useInviteScope();
  return useQuery({
    queryKey: queryKeys.programmeInvitations.mine(scope),
    queryFn: () => getJSON<ProgrammeInvitation[]>("/programmeInvitations/mine"),
    enabled,
  });
}

export function useInvitationByToken(token: string | null) {
  return useQuery({
    queryKey: queryKeys.programmeInvitations.byToken(token ?? ""),
    queryFn: () =>
      getJSON<InvitationTokenLookup>(`/programmeInvitations/byToken/${token}`),
    enabled: !!token,
  });
}

export interface CreateInvitationInput {
  email: string;
  name?: string;
  tradingName?: string;
}

export function useCreateProgrammeInvitation(programmeGuid: string) {
  const qc = useQueryClient();
  return useGuardedMutation({
    mutationFn: (input: CreateInvitationInput) =>
      postJSON<{ guid: string }>(`/programme/${programmeGuid}/invitations`, {
        email: input.email,
        name: input.name,
        tradingName: input.tradingName,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.programmeInvitations.all,
      });
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useGuardedMutation({
    mutationFn: (invitationGuid: string) =>
      postJSON<void>(`/programmeInvitations/${invitationGuid}/resend`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.programmeInvitations.all,
      });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useGuardedMutation({
    mutationFn: (invitationGuid: string) =>
      postJSON<void>(`/programmeInvitations/${invitationGuid}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.programmeInvitations.all,
      });
    },
  });
}

export function useAcceptInvitationByToken() {
  const qc = useQueryClient();
  return useGuardedMutation({
    mutationFn: (token: string) =>
      postJSON<{
        programmeUserGuid: string;
        programmeGuid: string;
        inviteStatus: InviteStatus;
      }>(`/programmeInvitations/byToken/${token}/accept`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.programmeInvitations.all,
      });
    },
  });
}
